import { setup, assign, assertEvent, and } from 'xstate';
import { LANE_SIZE, MAX_MAGICKA, STARTING_HEALTH, MAX_HAND_SIZE } from '../constants';
// ============ Helper Functions ============
function createEmptyBoard() {
    return {
        playerLane: Array(LANE_SIZE).fill(null),
        opponentLane: Array(LANE_SIZE).fill(null),
    };
}
function isPlayersTurn(context, playerId) {
    return context.players[context.currentPlayerIndex]?.id === playerId;
}
function getPlayerLane(context, playerId) {
    const playerIndex = context.players.findIndex((p) => p.id === playerId);
    return playerIndex === 0 ? context.board.playerLane : context.board.opponentLane;
}
function getOpponentLane(context, playerId) {
    const playerIndex = context.players.findIndex((p) => p.id === playerId);
    return playerIndex === 0 ? context.board.opponentLane : context.board.playerLane;
}
function findCreatureOnBoard(context, creatureId) {
    for (const lane of [context.board.playerLane, context.board.opponentLane]) {
        const creature = lane.find((c) => c?.id === creatureId);
        if (creature)
            return creature;
    }
    return null;
}
function hasBlockingGuards(context, targetPlayerId) {
    const targetLane = getPlayerLane(context, targetPlayerId);
    return targetLane.some((c) => c !== null && c.keywords.includes('guard'));
}
function removeCreatureFromBoard(context, creatureId) {
    for (const lane of [context.board.playerLane, context.board.opponentLane]) {
        for (let i = 0; i < lane.length; i++) {
            if (lane[i]?.id === creatureId) {
                lane[i] = null;
                return;
            }
        }
    }
}
// ============ Game Machine ============
export const gameMachine = setup({
    types: {
        context: {},
        events: {},
    },
    guards: {
        isPlayersTurn: ({ context, event }) => {
            if ('playerId' in event) {
                return isPlayersTurn(context, event.playerId);
            }
            return false;
        },
        canPlayCard: ({ context, event }) => {
            assertEvent(event, 'PLAY_CARD');
            const player = context.players.find((p) => p.id === event.playerId);
            if (!player)
                return false;
            const card = player.hand.find((c) => c.id === event.cardId);
            if (!card)
                return false;
            if (card.cost > player.magicka)
                return false;
            if (card.type !== 'creature')
                return false;
            const lane = getPlayerLane(context, event.playerId);
            if (lane[event.position] !== null)
                return false;
            return true;
        },
        canApplyItem: ({ context, event }) => {
            assertEvent(event, 'APPLY_ITEM');
            const player = context.players.find((p) => p.id === event.playerId);
            if (!player)
                return false;
            const card = player.hand.find((c) => c.id === event.cardId);
            if (!card)
                return false;
            if (card.cost > player.magicka)
                return false;
            if (card.type !== 'item' && card.type !== 'action')
                return false;
            const target = findCreatureOnBoard(context, event.targetCreatureId);
            if (!target)
                return false;
            return true;
        },
        canAttackCreature: ({ context, event }) => {
            assertEvent(event, 'ATTACK_CREATURE');
            const attacker = findCreatureOnBoard(context, event.attackerId);
            if (!attacker)
                return false;
            if (attacker.owner !== event.playerId)
                return false;
            if (!attacker.canAttack || attacker.hasAttacked)
                return false;
            const target = findCreatureOnBoard(context, event.targetId);
            if (!target)
                return false;
            // Check for guards - if there are guards, must attack them
            const targetOwner = target.owner;
            const opponentLane = getPlayerLane(context, targetOwner);
            const hasGuards = opponentLane.some((c) => c !== null && c.keywords.includes('guard'));
            if (hasGuards && !target.keywords.includes('guard'))
                return false;
            return true;
        },
        canAttackPlayer: ({ context, event }) => {
            assertEvent(event, 'ATTACK_PLAYER');
            const attacker = findCreatureOnBoard(context, event.attackerId);
            if (!attacker)
                return false;
            if (attacker.owner !== event.playerId)
                return false;
            if (!attacker.canAttack || attacker.hasAttacked)
                return false;
            if (hasBlockingGuards(context, event.targetPlayerId))
                return false;
            return true;
        },
        isGameOver: ({ context }) => {
            return context.players.some((p) => p.health <= 0);
        },
    },
    actions: {
        initializeGame: assign({
            players: ({ event }) => {
                assertEvent(event, 'START_GAME');
                return [event.player1, event.player2];
            },
            board: () => createEmptyBoard(),
            currentPlayerIndex: () => 0,
            turn: () => 1,
            winner: () => null,
        }),
        playCard: assign({
            players: ({ context, event }) => {
                assertEvent(event, 'PLAY_CARD');
                return context.players.map((player) => {
                    if (player.id !== event.playerId)
                        return player;
                    const cardIndex = player.hand.findIndex((c) => c.id === event.cardId);
                    const card = player.hand[cardIndex];
                    let clues = player.clues;
                    if (card.keywords.includes('investigate')) {
                        clues += 1;
                    }
                    return {
                        ...player,
                        magicka: player.magicka - card.cost,
                        hand: player.hand.filter((_, i) => i !== cardIndex),
                        clues
                    };
                });
            },
            board: ({ context, event }) => {
                assertEvent(event, 'PLAY_CARD');
                const player = context.players.find((p) => p.id === event.playerId);
                const card = player.hand.find((c) => c.id === event.cardId);
                const creature = {
                    id: `${card.id}-${Date.now()}`,
                    card,
                    currentPower: card.power ?? 0,
                    currentHealth: card.health ?? 0,
                    keywords: [...card.keywords],
                    canAttack: card.keywords.includes('charge'),
                    hasAttacked: false,
                    ward: card.keywords.includes('ward'),
                    owner: event.playerId,
                    equippedItems: [],
                };
                const playerIndex = context.players.findIndex((p) => p.id === event.playerId);
                const newBoard = { ...context.board };
                if (playerIndex === 0) {
                    newBoard.playerLane = [...context.board.playerLane];
                    newBoard.playerLane[event.position] = creature;
                }
                else {
                    newBoard.opponentLane = [...context.board.opponentLane];
                    newBoard.opponentLane[event.position] = creature;
                }
                return newBoard;
            },
        }),
        applyItem: assign({
            players: ({ context, event }) => {
                assertEvent(event, 'APPLY_ITEM');
                return context.players.map((player) => {
                    if (player.id !== event.playerId)
                        return player;
                    const cardIndex = player.hand.findIndex((c) => c.id === event.cardId);
                    const card = player.hand[cardIndex];
                    let clues = player.clues;
                    if (card.keywords.includes('investigate')) {
                        clues += 1;
                    }
                    return {
                        ...player,
                        magicka: player.magicka - card.cost,
                        hand: player.hand.filter((_, i) => i !== cardIndex),
                        clues
                    };
                });
            },
            board: ({ context, event }) => {
                assertEvent(event, 'APPLY_ITEM');
                const player = context.players.find((p) => p.id === event.playerId);
                const card = player.hand.find((c) => c.id === event.cardId);
                const newBoard = { ...context.board };
                for (const laneKey of ['playerLane', 'opponentLane']) {
                    newBoard[laneKey] = context.board[laneKey].map((creature) => {
                        if (!creature || creature.id !== event.targetCreatureId)
                            return creature;
                        const updatedCreature = { ...creature };
                        if (card.type === 'item') {
                            updatedCreature.currentPower += card.power ?? 0;
                            updatedCreature.currentHealth += card.health ?? 0;
                            if (card.keywords) {
                                updatedCreature.keywords = [...new Set([...creature.keywords, ...card.keywords])];
                            }
                            updatedCreature.equippedItems = [...creature.equippedItems, card];
                            // Radiance check
                            if (creature.keywords.includes('radiance')) {
                                updatedCreature.currentPower += 1;
                                updatedCreature.currentHealth += 1;
                            }
                        }
                        else if (card.type === 'action') {
                            // Damage spells
                            const cName = card.name;
                            if (cName === 'Interrogation') {
                                updatedCreature.currentHealth -= 2;
                            }
                            else if (cName === 'Sucker Punch') {
                                updatedCreature.currentHealth -= (player.magicka === 0 ? 3 : 1);
                            }
                            else if (cName.includes('Bolt') || cName.includes('Flare') || cName.includes('Explosive Pen')) {
                                updatedCreature.currentHealth -= 4;
                            }
                            else if (cName.includes('Javelin') || cName.includes('Big Sleep') || cName.includes('Fixed Fight')) {
                                updatedCreature.currentHealth = 0;
                            }
                        }
                        return updatedCreature;
                    });
                }
                // Remove dead creatures
                for (const laneKey of ['playerLane', 'opponentLane']) {
                    newBoard[laneKey] = newBoard[laneKey].map((c) => (c && c.currentHealth <= 0 ? null : c));
                }
                return newBoard;
            },
        }),
        attackCreature: assign({
            board: ({ context, event }) => {
                assertEvent(event, 'ATTACK_CREATURE');
                const newBoard = { ...context.board };
                const findAndUpdate = (id, updateFn) => {
                    for (const laneKey of ['playerLane', 'opponentLane']) {
                        newBoard[laneKey] = newBoard[laneKey].map((c) => {
                            if (!c || c.id !== id)
                                return c;
                            return updateFn(c);
                        });
                    }
                };
                const attacker = findCreatureOnBoard(context, event.attackerId);
                const defender = findCreatureOnBoard(context, event.targetId);
                const attackerPlayer = context.players.find(p => p.id === attacker.owner);
                const defenderPlayer = context.players.find(p => p.id === defender.owner);
                const getPowerBoost = (creature, player) => {
                    let boost = 0;
                    if (creature.keywords.includes('empowered') && player.clues > 0)
                        boost += 1;
                    if (creature.keywords.includes('overdrive') && player.magicka === 0)
                        boost += 2;
                    return boost;
                };
                // Handle Ward
                if (defender.ward) {
                    findAndUpdate(defender.id, (c) => ({ ...c, ward: false }));
                    findAndUpdate(attacker.id, (c) => ({ ...c, hasAttacked: true }));
                }
                else {
                    const attackerDamage = attacker.currentPower + getPowerBoost(attacker, attackerPlayer);
                    const defenderDamage = defender.currentPower + getPowerBoost(defender, defenderPlayer);
                    findAndUpdate(attacker.id, (c) => {
                        let newHealth = c.currentHealth - defenderDamage;
                        if (defender.keywords.includes('lethal') && defenderDamage > 0)
                            newHealth = 0;
                        return { ...c, currentHealth: newHealth, hasAttacked: true };
                    });
                    findAndUpdate(defender.id, (c) => {
                        let newHealth = c.currentHealth - attackerDamage;
                        if (attacker.keywords.includes('lethal') && attackerDamage > 0)
                            newHealth = 0;
                        return { ...c, currentHealth: newHealth };
                    });
                }
                // Remove dead creatures
                for (const laneKey of ['playerLane', 'opponentLane']) {
                    newBoard[laneKey] = newBoard[laneKey].map((c) => (c && c.currentHealth <= 0 ? null : c));
                }
                return newBoard;
            },
            players: ({ context, event }) => {
                assertEvent(event, 'ATTACK_CREATURE');
                const attacker = findCreatureOnBoard(context, event.attackerId);
                const defender = findCreatureOnBoard(context, event.targetId);
                const attackerPlayer = context.players.find(p => p.id === attacker.owner);
                const getPowerBoost = (creature, player) => {
                    let boost = 0;
                    if (creature.keywords.includes('empowered') && player.clues > 0)
                        boost += 1;
                    if (creature.keywords.includes('overdrive') && player.magicka === 0)
                        boost += 2;
                    return boost;
                };
                const attackerDamage = attacker.currentPower + getPowerBoost(attacker, attackerPlayer);
                // Handle Drain
                if (attacker.keywords.includes('drain') && !defender.ward) {
                    return context.players.map((p) => {
                        if (p.id !== attacker.owner)
                            return p;
                        return { ...p, health: Math.min(p.health + attackerDamage, STARTING_HEALTH) };
                    });
                }
                // Handle Breakthrough - excess damage to player
                if (attacker.keywords.includes('breakthrough') && !defender.ward) {
                    const excessDamage = Math.max(0, attackerDamage - defender.currentHealth);
                    if (excessDamage > 0) {
                        return context.players.map((p) => {
                            if (p.id !== defender.owner)
                                return p;
                            return { ...p, health: Math.max(0, p.health - excessDamage) };
                        });
                    }
                }
                return context.players;
            },
        }),
        attackPlayer: assign({
            board: ({ context, event }) => {
                assertEvent(event, 'ATTACK_PLAYER');
                const newBoard = { ...context.board };
                for (const laneKey of ['playerLane', 'opponentLane']) {
                    newBoard[laneKey] = newBoard[laneKey].map((c) => {
                        if (!c || c.id !== event.attackerId)
                            return c;
                        return { ...c, hasAttacked: true };
                    });
                }
                return newBoard;
            },
            players: ({ context, event }) => {
                assertEvent(event, 'ATTACK_PLAYER');
                const attacker = findCreatureOnBoard(context, event.attackerId);
                const player = context.players.find(p => p.id === attacker.owner);
                const getPowerBoost = (creature, player) => {
                    let boost = 0;
                    if (creature.keywords.includes('empowered') && player.clues > 0)
                        boost += 1;
                    if (creature.keywords.includes('overdrive') && player.magicka === 0)
                        boost += 2;
                    return boost;
                };
                const damage = attacker.currentPower + getPowerBoost(attacker, player);
                return context.players.map((p) => {
                    if (p.id === event.targetPlayerId) {
                        return { ...p, health: Math.max(0, p.health - damage) };
                    }
                    // Handle Drain
                    if (p.id === attacker.owner && attacker.keywords.includes('drain')) {
                        return { ...p, health: Math.min(p.health + damage, STARTING_HEALTH) };
                    }
                    return p;
                });
            },
        }),
        endTurn: assign({
            currentPlayerIndex: ({ context }) => (context.currentPlayerIndex + 1) % context.players.length,
            turn: ({ context }) => (context.currentPlayerIndex === 1 ? context.turn + 1 : context.turn),
            players: ({ context }) => {
                const nextPlayerIndex = (context.currentPlayerIndex + 1) % context.players.length;
                return context.players.map((p, i) => {
                    if (i !== nextPlayerIndex)
                        return p;
                    const newMaxMagicka = Math.min(p.maxMagicka + 1, MAX_MAGICKA);
                    // Draw a card
                    const newDeck = [...p.deck];
                    const drawnCard = newDeck.pop();
                    const isHandFull = p.hand.length >= MAX_HAND_SIZE;
                    return {
                        ...p,
                        maxMagicka: newMaxMagicka,
                        magicka: newMaxMagicka,
                        deck: newDeck,
                        hand: (drawnCard && !isHandFull) ? [...p.hand, drawnCard] : p.hand,
                    };
                });
            },
            board: ({ context }) => {
                // Reset creature states for next player
                const nextPlayerIndex = (context.currentPlayerIndex + 1) % context.players.length;
                const newBoard = { ...context.board };
                const laneKey = nextPlayerIndex === 0 ? 'playerLane' : 'opponentLane';
                newBoard[laneKey] = context.board[laneKey].map((c) => {
                    if (!c)
                        return null;
                    return { ...c, canAttack: true, hasAttacked: false };
                });
                return newBoard;
            },
        }),
        setWinner: assign({
            winner: ({ context }) => {
                const loser = context.players.find((p) => p.health <= 0);
                if (!loser)
                    return null;
                return context.players.find((p) => p.id !== loser.id)?.id ?? null;
            },
        }),
    },
}).createMachine({
    id: 'game',
    initial: 'idle',
    context: {
        gameId: '',
        players: [],
        board: createEmptyBoard(),
        currentPlayerIndex: 0,
        turn: 1,
        winner: null,
    },
    states: {
        idle: {
            on: {
                START_GAME: {
                    target: 'playing',
                    actions: 'initializeGame',
                },
            },
        },
        playing: {
            initial: 'playerTurn',
            always: {
                guard: 'isGameOver',
                target: 'gameOver',
                actions: 'setWinner',
            },
            states: {
                playerTurn: {
                    on: {
                        PLAY_CARD: {
                            guard: and(['isPlayersTurn', 'canPlayCard']),
                            actions: 'playCard',
                        },
                        APPLY_ITEM: {
                            guard: and(['isPlayersTurn', 'canApplyItem']),
                            actions: 'applyItem',
                        },
                        ATTACK_CREATURE: {
                            guard: and(['isPlayersTurn', 'canAttackCreature']),
                            actions: 'attackCreature',
                        },
                        ATTACK_PLAYER: {
                            guard: and(['isPlayersTurn', 'canAttackPlayer']),
                            actions: 'attackPlayer',
                        },
                        END_TURN: {
                            guard: 'isPlayersTurn',
                            actions: 'endTurn',
                            target: 'playerTurn',
                        },
                    },
                },
            },
        },
        gameOver: {
            type: 'final',
        },
    },
});
